import axios from 'axios';
import { isEmpty } from 'lodash';
import { uniqueId } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { config } from '../../config/Config';
import { DataContext } from '../../context/DataContext';
import { createDate } from '../../helper/date/createDateWithTimezone';
import { buildQueryString, parseQueryString } from '../../helper/utils';
import Filters from './filters/Filters';

import ShukahLogDetail from './ShukahLogDetail';
import useColumns from './useColumns';

const ShukahLog = () => {
  const { search } = useLocation();
  const queryString = parseQueryString(search);
  const { t } = useTranslation();

  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [limit, setLimit] = useState(100);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [parsingQuery, setParsingQuery] = useState(true);
  const { columns } = useColumns({ logs });
  const { currentTimezone } = useContext(DataContext);
  const [newRender, setNewRender] = useState(uniqueId());

  const handlePageChange = async (page) => {
    await setCurrentPage(page);
    setNewRender(uniqueId());
  };

  const handleDateFilter = (value) => {
    if (value) {
      const newFilter = {
        ...filters,
        fromDate: new Date(value[0]).getTime(),
        toDate: new Date(value[1]).getTime(),
      };
      setFilters(newFilter);
    } else {
      const newFilter = {
        ...filters,
        fromDate: null,
        toDate: null,
      };
      setFilters(newFilter);
      setDateRange([null, null]);
    }
  };

  useEffect(() => {
    if (filters?.fromDate && filters?.toDate) {
      const dateValue = [new Date(+filters?.fromDate), new Date(+filters?.toDate)];
      setDateRange(dateValue);
    } else {
      setDateRange([null, null]);
    }
  }, [filters?.fromDate, filters?.toDate]);

  const toggleModal = (visible) => {
    setModalVisible(visible);
    if (!visible) {
      setSelectedLog(null);
    }
  };

  const handleToggleModal = (id) => {
    toggleModal(true);
    setSelectedLog(id);
  };

  const getShukahLogs = async (_filter = {}) => {
    const newFilters = {
      filters: {
        ...filters,
        fromDate: filters.fromDate
          ? new Date(createDate(+filters?.fromDate, currentTimezone)).getTime()
          : '',
        toDate: filters.toDate
          ? new Date(createDate(+filters?.toDate, currentTimezone)).getTime()
          : '',
      },
      sort: -1,
      limit: limit,
      page: currentPage,
    };

    // const filter = newFilters;
    const filter = isEmpty(_filter) ? newFilters : _filter;

    const newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      `?${buildQueryString({ ...filter })}`;

    window.history.pushState({ path: newurl }, '', newurl);

    const finalizeFilter = {
      fromDate: null,
      toDate: null,
      status: [],
      types: [],
      order_number: '',
      id_number: '',
      customer_phone: '',
      restaurant_name: '',
      payment_method: '',
    };

    if (filter?.filters?.status && filter?.filters?.status?.length > 0) {
      filter?.filters?.status?.forEach((st) => {
        finalizeFilter.status?.push(+st.value);
      });
    }

    if (filter?.filters?.types && filter?.filters?.types?.length > 0) {
      filter?.filters?.types?.forEach((st) => {
        finalizeFilter.types?.push(st.value);
      });
    }

    if (filter?.filters?.fromDate) {
      finalizeFilter.fromDate = +filter?.filters?.fromDate;
    }

    if (filter?.filters?.toDate) {
      finalizeFilter.toDate = +filter?.filters?.toDate;
    }

    if (filter?.filters?.order_number) {
      finalizeFilter.order_number = filter?.filters?.order_number;
    }

    if (filter?.filters?.client_phone) {
      finalizeFilter.customer_phone = filter?.filters?.client_phone;
    }

    if (filter?.filters?.driver_id) {
      finalizeFilter.id_number = filter?.filters?.driver_id;
    }

    if (filter?.filters?.merchant_name) {
      finalizeFilter.restaurant_name = filter?.filters?.merchant_name;
    }

    if (filter?.filters?.payment_method) {
      finalizeFilter.payment_method = filter?.filters?.payment_method.value;
    }

    if (loading) return;
    setLoading(true);
    const response = await axios.get(`${config.API_URL.SHUKAH_LOGS.LIST}`, {
      params: {
        ...filter,
        filters: finalizeFilter,
      },
      headers: config.headers,
    });

    const { data = {}, total } = response?.data;

    if (total >= 0) {
      setLogs(data);
      await setTotal(total);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!parsingQuery) {
      getShukahLogs();
    }
  }, [limit, parsingQuery, newRender]);

  useEffect(() => {
    if (queryString.page) {
      setCurrentPage(+queryString.page);
    }
    if (queryString.filters) {
      const filter = { ...queryString.filters };
      if (filter.fromDate) {
        filter.fromDate = +filter.fromDate;
      }
      if (filter.toDate) {
        filter.toDate = +filter.toDate;
      }
      setFilters(filter);
    }
    if (queryString.limit > 0) {
      setLimit(+queryString.limit);
    }
    setParsingQuery(false);
  }, []);

  const tableHeight = window.innerHeight - 330;

  const conditionalRowStyles = [
    {
      when: (row) =>
        Number(row.status) !== 200 || Number(row.status) !== 201 || Number(row.status) !== 304,

      style: {
        backgroundColor: '#F7DDDE',
      },
    },
    {
      when: (row) =>
        Number(row.status) === 200 || Number(row.status) === 201 || Number(row.status) === 304,
      style: {
        backgroundColor: '#D4E9D8',
      },
    },
  ];

  const selectHandler = (e, key) => {
    const newFilters = {
      ...filters,
      [key]: e,
    };
    setFilters(newFilters);
  };

  const onGetShukahLog = async () => {
    await setCurrentPage(1);
    const newFilters = {
      filters: {
        ...filters,
        fromDate: filters.fromDate
          ? new Date(createDate(+filters?.fromDate, currentTimezone)).getTime()
          : '',
        toDate: filters.toDate
          ? new Date(createDate(+filters?.toDate, currentTimezone)).getTime()
          : '',
      },
      sort: -1,
      limit: limit,
      page: 1,
    };

    getShukahLogs(newFilters);
  };

  return (
    <div className="rtl">
      <Filters
        dateRange={dateRange}
        filters={filters}
        handleDateFilter={handleDateFilter}
        loading={loading}
        selectHandler={selectHandler}
        getShukahLogs={onGetShukahLog}
      />
      <DataTable
        noDataComponent={<div style={{ padding: '24px' }}>{t('orders_no_records')}</div>}
        highlightOnHover
        columns={columns}
        data={logs}
        fixedHeader
        pagination
        contextMessage={{ singular: t('item'), plural: t('items'), message: t('selected') }}
        fixedHeaderScrollHeight={tableHeight.toString() + 'px'}
        paginationPerPage={limit}
        paginationRowsPerPageOptions={[100, 200, 400, 600]}
        onChangeRowsPerPage={(currentRowsPerPage) => setLimit(+currentRowsPerPage)}
        progressPending={loading}
        progressComponent={<div className="wait">{t('please_wait')}</div>}
        paginationTotalRows={total}
        paginationServer
        onChangePage={handlePageChange}
        paginationComponentOptions={{
          rowsPerPageText: t('total_record'),
          rangeSeparatorText: t('from'),
          noRowsPerPage: false,
          selectAllRowsItem: false,
        }}
        selectableRows
        onRowExpandToggled={(toggleState, row) => [
          toggleState
            ? setSelectedId((p) => [...p, row._id])
            : setSelectedId(selectedId.filter((i) => i !== row._id)),
        ]}
        conditionalRowStyles={conditionalRowStyles}
        onRowClicked={(row) => handleToggleModal(row?._id)}
        paginationDefaultPage={currentPage}
        selectableRowsVisibleOnly
        selectableRowSelected={(row) => row.isSelected}
        persistTableHead
        selectableRowsHighlight
        direction={'rtl'}
        customStyles={{
          headCells: {
            style: {
              fontWeight: 'bold',
              fontSize: 12,
              background: '#F9F9F9',
            },
          },
          rows: {
            style: {
              cursor: 'pointer',
              fontSize: 12,
            },
          },
          header: {
            style: {
              minHeight: 0,
            },
          },
        }}
      />
      <ShukahLogDetail id={selectedLog} visible={modalVisible} toggleModal={toggleModal} />
    </div>
  );
};

export default ShukahLog;
